// Baruch College (CUNY): public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Baruch's Common Data Set 2024-2025, the newest edition posted.
//    Departure: the Office of Institutional Research's Common Data Sets
//    page still lists editions only through 2022-2023 (on SharePoint); the
//    2024-2025 file (CDS-2024-2025-FINAL-Jun-12.pdf) is on ir.baruch.cuny.edu
//    and linked from the office's Consumer Information page. No 2025-2026
//    edition was found on baruch.cuny.edu, so the counts are for Fall 2024,
//    a year older than most pages here. Read from it: C1 applicants, admits
//    and enrollees for Fall 2024 (the residency table fills only the
//    enrolled row, so this page shows no residency split), C8 (SAT or ACT
//    considered if submitted, written for Fall 2026; no score date),
//    C21/C22 (no early decision, no early action), G1/G5 costs for
//    2025-2026. Neither Baruch nor CUNY posts a 2026-2027 budget yet: the
//    financial aid office links CUNY's Student Budgets page, which shows
//    2025-2026, so the cost is for 2025-2026. The Bursar's Tuition page
//    (adminfinance.baruch.cuny.edu, "Effective Fall 2025") gives the same
//    tuition, $3,465 a semester for New York State residents and $620 a
//    unit for others, and $265.60 a semester in full-time fees.
//  - Baruch Undergraduate Admissions, How to Apply: the fall deadline and
//    notification table (no year given; it is the page as posted while the
//    fall 2027 application is open, with Macaulay dates for fall 2026
//    applications), the review, SEEK, and Macaulay at Baruch.
//    Quirk: its test score section is headed "Fall 2023 through Spring 2027
//    admission"; CUNY's Standardized Testing Policy page states the policy
//    with no end date, so the page follows CUNY and says so.
//  - Macaulay Honors College, Early Decision Program (binding terms; Baruch
//    is one of the colleges it covers).
//  - College Scorecard API, school id 190512 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Baruch College Common Data Set 2024-2025',
        url: 'https://ir.baruch.cuny.edu/wp-content/uploads/sites/23/2025/07/CDS-2024-2025-FINAL-Jun-12.pdf',
        read: '2026-10-01',
    },
    howto: {
        label: 'Baruch Undergraduate Admissions, How to Apply',
        url: 'https://enrollmentmanagement.baruch.cuny.edu/undergraduate-admissions/how-to-apply/',
        read: '2026-10-01',
    },
    testPolicy: {
        label: 'CUNY Admissions, Standardized Testing Policy',
        url: 'https://www.cuny.edu/admissions/undergraduate/apply/covid-19-standardized-tests/',
        read: '2026-10-01',
    },
    macaulay: {
        label: 'Macaulay Honors College, Early Decision Program',
        url: 'https://macaulay.cuny.edu/admissions/applying-to-macaulay/early-decision-program/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190512-CUNY-Bernard-M-Baruch-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'cuny-bernard-m-baruch-college',
    kbId: 'cuny_bernard_m_baruch_college',
    name: 'CUNY Baruch College',
    shortName: 'Baruch',
    aliases: ['Baruch', 'Bernard M. Baruch College'],
    location: 'New York, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Baruch reputation management toolkit, logos); decoration only.
    branding: {
        primary: '#05336B',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/cuny_bernard_m_baruch_college_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 28111,
        admitted: 13357,
        enrolled: 2532,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Baruch\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by school or major. Its newest posted edition is 2024-2025, so these counts are for Fall 2024.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'Baruch has no early decision or early action plan of its own; the Common Data Set answers no to both. Macaulay Honors College at Baruch has its own binding Early Decision deadline, listed with the deadlines below.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Baruch through the CUNY application, which takes up to six CUNY colleges for one $65 fee. Two programs ask for more:',
        rows: [
            { school: 'Macaulay Honors College at Baruch', requirement: 'The Macaulay application, complete high school transcripts and two of the three Macaulay essays, by October 26 for Early Decision (binding) or November 16 for regular admission, both at 6 pm. A resume is optional. Macaulay applicants may also be considered for the Baruch Honors program and other merit scholarships.', source: 'howto' },
            { school: 'SEEK', requirement: 'Mark your interest in SEEK on the freshman application. If you meet the academic requirements, you are asked for documents that show financial eligibility.', source: 'howto' },
        ],
        source: 'howto',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 1' },
        rows: [
            { plan: 'First-year (U.S. educated)', deadline: 'February 1', decision: 'Rolling, January to July', binding: false },
            { plan: 'First-year, international', deadline: 'December 1', decision: 'Rolling, January to July', binding: false },
            { plan: 'Macaulay Honors College, Early Decision', deadline: 'October 26', decision: 'December 18', binding: true },
            { plan: 'Macaulay Honors College, regular admission', deadline: 'November 16', decision: 'March 11', binding: false },
        ],
        note: 'Baruch admits on a rolling basis and recommends applying by the February 1 priority deadline, with the application and supporting documents. Decisions are emailed on a rolling basis after that date, and later applications are considered as space allows. International applicants send supporting documents by December 15. Macaulay regular applicants send letters of recommendation by November 30 and commit by May 3.',
        source: 'howto',
        binding: 'Macaulay Early Decision is binding: if admitted, you commit to attending Macaulay and withdraw all other college applications, and you may not defer. The general Baruch application is not binding.',
        bindingSource: 'macaulay',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CUNY is test-optional: you choose whether to send SAT or ACT scores, and leaving them out cannot be used to deny you admission. Decisions rest mainly on your high school record: your average, your course grades and the rigor of your courses. Baruch\'s own How to Apply page still heads this policy "Fall 2023 through Spring 2027"; CUNY\'s policy page gives no end date.',
        source: 'testPolicy',
        cdsLine: 'The Common Data Set 2024-2025 also marks the SAT or ACT as considered if submitted, not required, for fall 2026 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'howto',
    },

    cost: {
        year: '2025-2026',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6930, 18600] },
            { item: 'Required fees', amounts: [531, 531] },
            { item: 'Housing (not living at home)', amounts: [17928, 17928] },
            { item: 'Food', amounts: [4332, 4332] },
            { item: 'Books and supplies', amounts: [1500, 1500] },
            { item: 'Transportation', amounts: [1188, 1188] },
            { item: 'Other expenses', amounts: [9378, 9378] },
        ],
        totals: [41787, 53457],
        note: 'For a full-time student living away from home; the Common Data Set lists no on-campus housing. Living at home, it puts the costs beyond tuition and fees at $9,025. These are 2025-2026 figures, the newest year Baruch or CUNY posts. Out-of-state tuition applies to students who are not New York State residents.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 657,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: 3285 },
                { income: '$75,001 to $110,000', amount: 6088 },
                { income: 'Over $110,000', amount: 10728 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 and $30,001 to $48,000 bands. Your own price comes from Baruch\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
